import React from 'react';

import { widgetsConfigurations } from 'config/widgetConfigurations/widgetsConfigurations';
import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseWidget } from 'hooks/useBaseWidget';

import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';
import { BarChart } from 'components/common/charts/barChart/barChart';

import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/style/table.module.scss';
import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { AddToReport } from 'components/Dashboards/Reporting/AddToReport/AddToReport';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { Narrative } from 'components/Dashboards/Reporting/WidgetNarrativeWrapper/Narrative';
import { PinboardPills } from 'components/Dashboards/Reporting/pinboardPills/PinboardPills';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { Select } from 'components/DesignSystem/Select';
import {
    Aggregation,
    aggregationOptions,
} from 'components/widgets/ResourcesAnalysisChart/ResourcesAnalysisChart.consts';
import { useResourcesAnalysisChartLogic } from 'components/widgets/ResourcesAnalysisChart/ResourcesAnalysisChart.logic';
import { useResourcesAnalysisChartProvider } from 'components/widgets/ResourcesAnalysisChart/ResourcesAnalysisChart.provider';
import { Boxes } from 'components/widgets/ResourcesAnalysisChart/components/Boxes';
import localClasses from 'components/widgets/RunRateChart/productivityRate.module.scss';

const { title: defaultTitle, id, componentKey, endpoint } = widgetsConfigurations.resourcesAnalysisChart;

export const ResourcesAnalysisChart = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    compareVersionId,
    globalFilterData,
    localFilters,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,

    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title = defaultTitle,
    setIsPayloadReady,
}: IComponentProps) => {
    const { ui, api } = useResourcesAnalysisChartProvider({
        widgetId,
        latestVersionId,
        localFilters,
        globalFilterData,
        compareVersionId,
        componentKey,
    });

    const { subTitle, route, editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
        ui,
        reportWidgetRef,
        apiPayload: api.getResourceChart.payload,
        widgetEndpoint: endpoint,
    });

    const { logic } = useResourcesAnalysisChartLogic({
        ui,
        api,
        title,
        subTitle,
        latestVersionId,
        widgetViewMode,
        setIsPayloadReady,
    });

    return (
        <WidgetWithTitle
            id={id}
            title={<WidgetTitle title={editableTitle} setTitle={setEditableTitle} widgetViewMode={widgetViewMode} />}
            className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
            titleComponents={[
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                <AddWidget
                    key={'AddWidget'}
                    componentKey={componentKey}
                    title={title}
                    projectId={projectId}
                    contractId={contractId}
                    widgetId={widgetId}
                    route={route}
                    localFilters={ui.state}
                    shouldHide={true}
                />,
                <AddToReport
                    key={'addWidgetToReport'}
                    widgetId={widgetId}
                    componentKey={componentKey}
                    projectId={projectId}
                    contractId={contractId}
                    uiState={ui.state}
                    shouldHide={WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode)}
                    apiPayload={api.getResourceChart.payload}
                    widgetEndpoint={endpoint}
                />,
                <ExportDropdown
                    key={'export'}
                    title={title}
                    subTitle={subTitle}
                    handleAPICsvExport={logic.handleCsvExport}
                />,
                <TooltipIcon key={'info'} tooltip={api.getResourceChart.instance.data?.info} />,
                !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                    <ThreeDotsMenu
                        widgetViewMode={widgetViewMode}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        shareTitle={title}
                        shareLink={route}
                        projectId={projectId}
                        contractId={contractId}
                    />
                ),
            ]}
            titleFilters={[
                <ReportingVersionAndPills
                    key={'ReportingVersionAndPills'}
                    selectedProjectAndContract={widgetSelectedProjectAndContract}
                    pills={ui.pills}
                    widgetViewMode={widgetViewMode}
                />,
                <Narrative key={'narrative'} editNarrative={editNarrative} />,
                <PinboardPills
                    key={'pinboardPills'}
                    pills={ui.pills}
                    widgetViewMode={widgetViewMode}
                    isInPinboard={ui.isInPinboard}
                />,
                !WidgetViewModeUtils.isReportOrPinboardView(widgetViewMode) && (
                    <div key={'filters'} className={classes.filters}>
                        <div className={classes.selectWrapper}>
                            <div className={classes.title}>Aggregation</div>
                            <Select<ISelectOption<Aggregation>>
                                id={'aggregation-filter'}
                                value={ui.state.aggregation}
                                options={aggregationOptions}
                                onChange={(value) =>
                                    value && ui.setters.updateResourcesAnalysisStore({ aggregation: value })
                                }
                            />
                        </div>
                    </div>
                ),
            ]}
        >
            <div className={localClasses.resourceAnalysisChartContainer}>
                <Boxes data={api.getResourceChart.instance.data} />
                <div className={localClasses.container}>
                    {logic.options && (
                        <BarChart
                            options={logic.options}
                            series={logic.series}
                            isFetching={api.getResourceChart.instance.isFetching}
                            noData={!api.getResourceChart.instance.isFetching && !api.getResourceChart.instance.data}
                            height={logic.chartHeight}
                        />
                    )}
                </div>
            </div>
        </WidgetWithTitle>
    );
};
